import { pickActiveEmailForMdmBrand } from "@/lib/cat/mdm-email-brand";
import type {
  MdmContactUse,
  PhoneContact,
} from "@/lib/cat/individual-profile.types";
import type { MdmContact } from "@/types/cat/mdm-account.types";

export function parseMdmContactUse(
  use: string | null | undefined,
): MdmContactUse {
  const normalized = use?.trim().toLowerCase();
  if (normalized === "work") return "work";
  if (normalized === "home") return "home";
  return "unknown";
}

function isActiveContact(contact: MdmContact): boolean {
  const status = contact.status?.trim().toLowerCase();
  return !status || status === "actif";
}

function toPhoneContact(
  value: string | undefined,
  use: string | undefined,
): PhoneContact {
  return {
    value: value?.trim() ?? "",
    use: parseMdmContactUse(use),
  };
}

export type ExtractedPhones = {
  fixedPhone: PhoneContact;
  mobilePhone: PhoneContact;
};

export function extractPhonesFromMdmContacts(
  contacts: MdmContact[] | undefined,
): ExtractedPhones {
  const empty: ExtractedPhones = {
    fixedPhone: { value: "", use: "unknown" },
    mobilePhone: { value: "", use: "unknown" },
  };
  if (!contacts?.length) return empty;

  const active = contacts.filter(isActiveContact);
  const phones = active.filter((c) => c.channel === "phone");
  const mobiles = active.filter((c) => c.channel === "mobile");

  const workPhone = phones.find((c) => parseMdmContactUse(c.use) === "work");
  const homePhone = phones.find((c) => parseMdmContactUse(c.use) === "home");

  let fixedPhone: MdmContact | undefined = workPhone ?? phones[0];
  let mobilePhone: MdmContact | undefined =
    mobiles[0] ??
    (homePhone && homePhone !== fixedPhone ? homePhone : phones[1]);

  if (!fixedPhone && mobilePhone && mobiles.length === 0) {
    fixedPhone = mobilePhone;
    mobilePhone = undefined;
  }

  return {
    fixedPhone: toPhoneContact(fixedPhone?.value, fixedPhone?.use),
    mobilePhone: toPhoneContact(mobilePhone?.value, mobilePhone?.use),
  };
}

export function extractPrimaryEmailFromMdmContacts(
  contacts: MdmContact[] | undefined,
  mdmBrandCode?: string | null,
): string {
  return pickActiveEmailForMdmBrand(contacts, mdmBrandCode ?? null);
}

export function buildMdmContactsFromPhones(input: {
  email?: string;
  emailBrandCode?: string | null;
  fixedPhone?: PhoneContact;
  mobilePhone?: PhoneContact;
  includeContactStatus?: boolean;
  preserveEmailBrandCase?: boolean;
}): MdmContact[] {
  const result: MdmContact[] = [];
  const includeStatus = input.includeContactStatus ?? true;

  const email = input.email?.trim();
  if (email) {
    const contact: MdmContact = {
      channel: "email",
      use: "home",
      value: email,
    };
    if (includeStatus) {
      contact.status = "actif";
    }
    const brand = input.emailBrandCode?.trim();
    if (brand) {
      contact.brand = input.preserveEmailBrandCase
        ? brand
        : brand.toUpperCase();
    }
    result.push(contact);
  }

  const fixed = input.fixedPhone?.value.trim();
  if (fixed) {
    const contact: MdmContact = {
      channel: "phone",
      use: input.fixedPhone?.use ?? "unknown",
      value: fixed,
    };
    if (includeStatus) {
      contact.status = "actif";
    }
    result.push(contact);
  }

  const mobile = input.mobilePhone?.value.trim();
  if (mobile) {
    const contact: MdmContact = {
      channel: "mobile",
      use: input.mobilePhone?.use ?? "unknown",
      value: mobile,
    };
    if (includeStatus) {
      contact.status = "actif";
    }
    result.push(contact);
  }

  return result;
}

export function formatPhoneUseLabel(use: MdmContactUse): string {
  if (use === "work") return "Professionnel";
  if (use === "home") return "Personnel";
  return "Non renseigné";
}

export function formatPhoneWithUseLabel(
  value: string,
  use: MdmContactUse,
): string {
  const trimmed = value.trim();
  if (!trimmed) return "";
  if (use === "unknown") return trimmed;
  return `${trimmed} (${formatPhoneUseLabel(use)})`;
}
